/**
 * Scopes tenant-authored CSS to the policy container so it wins over the bundled
 * .policy-template-content rules without leaking across the app:
 *  - element selectors are prefixed with `#<containerId>` (id raises specificity)
 *  - body/html/:root become page-level defaults applied to the content elements
 *    via a zero-specificity :where() wrapper, so they beat the bundled rules
 *    (which set color/font directly, and direct always beats inherited) yet still
 *    yield to the tenant's own element selectors.
 *
 * Implemented as a small dependency-free scanner rather than via postcss: postcss
 * pulls in nanoid, whose ESM/CJS interop breaks under this CRA5/webpack build and
 * throws at runtime in the browser (TypeError: nanoid is not a function).
 */

const ROOT_SELECTORS = new Set(['body', 'html', ':root']);

// The container class the bundled vendor.css/Policy.css rules target.
const CONTENT_CLASS = 'policy-template-content';

// At-rules whose bodies hold style rules that themselves need scoping. Others
// (@font-face, @keyframes, @import, @charset) are emitted unchanged.
const NESTED_AT_RULES = new Set(['media', 'supports', 'container', 'layer']);

// body/html/:root → the content root and all its descendants at the specificity
// of the container id alone (`:where()` adds nothing). Sits above the bundled
// rules (0,1,1) but below the tenant's own element rules (1,0,1).
const scopeSingleSelector = (
  selector: string,
  containerSelector: string
): string => {
  const trimmed = selector.trim();
  if (!trimmed) return trimmed;
  if (ROOT_SELECTORS.has(trimmed.toLowerCase())) {
    return `${containerSelector} :where(.${CONTENT_CLASS}, .${CONTENT_CLASS} *)`;
  }
  if (trimmed.startsWith(containerSelector)) return trimmed;
  return `${containerSelector} ${trimmed}`;
};

// Prefix every comma-separated selector in a selector list.
const scopeSelectorList = (
  selectorList: string,
  containerSelector: string
): string =>
  selectorList
    .split(',')
    .map((s) => scopeSingleSelector(s, containerSelector))
    .join(', ');

// Appends `!important` to each declaration in a rule body so tenant styles
// override the app's bundled `!important` rules. Splits on top-level `;` only
// (no nested blocks exist inside a leaf style rule), preserves comments, and
// leaves declarations that are already `!important` untouched.
const forceImportant = (body: string): string =>
  body
    .split(';')
    .map((part) => {
      const colon = part.indexOf(':');
      // No colon → whitespace, comment, or trailing fragment: keep as-is.
      if (colon === -1) return part;
      if (/!\s*important/i.test(part)) return part;
      // Preserve trailing whitespace/comment after the value.
      const trimmedEnd = part.replace(/\s+$/, '');
      const trailing = part.slice(trimmedEnd.length);
      return `${trimmedEnd} !important${trailing}`;
    })
    .join(';');

export const scopeTenantCss = (
  css: string | null | undefined,
  containerId: string | null | undefined
): string => {
  if (!css || !css.trim() || !containerId) return '';
  const containerSelector = `#${containerId}`;

  let out = '';
  let i = 0;
  const n = css.length;
  // Buffer of text since the last `{`, `}`, or top — i.e. a pending selector or
  // at-rule prelude.
  let buf = '';

  const flushOpaque = () => {
    out += buf;
    buf = '';
  };

  while (i < n) {
    const ch = css[i];

    // Comments: if the pending buffer is only whitespace (we're between rules),
    // emit the comment directly so it can't pollute the next selector. Otherwise
    // it's inside a selector prelude — keep it in the buffer.
    if (ch === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2);
      const stop = end === -1 ? n : end + 2;
      if (buf.trim() === '') {
        out += buf + css.slice(i, stop);
        buf = '';
      } else {
        buf += css.slice(i, stop);
      }
      i = stop;
      continue;
    }

    if (ch === '{') {
      const prelude = buf.trim();
      buf = '';

      if (prelude.startsWith('@')) {
        const name = prelude.slice(1).split(/[\s({]/)[0].toLowerCase();
        if (NESTED_AT_RULES.has(name)) {
          // Keep the at-rule prelude, recurse into its body so inner rules scope.
          const bodyEnd = matchBrace(css, i);
          const inner = css.slice(i + 1, bodyEnd);
          out += `${prelude} { `;
          out += scopeTenantCss(inner, containerId);
          out += '}';
          i = bodyEnd + 1;
          continue;
        }
        // Non-nested at-rule (@font-face, @keyframes…): emit block unchanged.
        const bodyEnd = matchBrace(css, i);
        out += `${prelude} {${css.slice(i + 1, bodyEnd)}}`;
        i = bodyEnd + 1;
        continue;
      }

      // Normal style rule: scope its selector list, then copy its declaration
      // block through to the matching `}`, forcing each declaration to
      // !important so tenant styles win over the app's bundled !important rules
      // (e.g. index.css `p { font-family: ... !important }`).
      const bodyEnd = matchBrace(css, i);
      out += `${scopeSelectorList(prelude, containerSelector)} {${forceImportant(
        css.slice(i + 1, bodyEnd)
      )}}`;
      i = bodyEnd + 1;
      continue;
    }

    if (ch === '}') {
      // Stray close brace (e.g. when scanning an at-rule body recursively).
      flushOpaque();
      i += 1;
      continue;
    }

    if (ch === ';' && buf.trim().startsWith('@')) {
      // Statement at-rule with no block (@import, @charset): emit verbatim.
      out += `${buf};`;
      buf = '';
      i += 1;
      continue;
    }

    buf += ch;
    i += 1;
  }

  flushOpaque();
  return out;
};

// Returns the index of the `}` matching the `{` at `open`, accounting for
// nested braces, strings, and comments. Falls back to end-of-string.
function matchBrace(css: string, open: number): number {
  let depth = 0;
  for (let j = open; j < css.length; j += 1) {
    const c = css[j];
    if (c === '/' && css[j + 1] === '*') {
      const end = css.indexOf('*/', j + 2);
      j = end === -1 ? css.length : end + 1;
      continue;
    }
    if (c === '"' || c === "'") {
      const end = css.indexOf(c, j + 1);
      j = end === -1 ? css.length : end;
      continue;
    }
    if (c === '{') depth += 1;
    else if (c === '}') {
      depth -= 1;
      if (depth === 0) return j;
    }
  }
  return css.length;
}
